import React from 'react';
import { PpaTableRowType } from 'types/ppa';
import {
    formatPpaSimulationPeriod,
    getPpaColumnTextValue,
} from 'utils/ppaHelpers';
import ActionMenuCell from '../cells/ActionMenuCell';
import CreatedByCell from '../cells/CreatedByCell';
import ProjectNameCell from '../cells/ProjectNameCell';
import ScopeCell from '../cells/ScopeCell';
import StatusCell from '../cells/StatusCell';
import type { InternalTableRecord } from '../types';

export type ColumnRenderersContext = {
    isProjectExpanded: (projectKey: string) => boolean;
    showAnalyticsCheckbox: boolean;
    onToggleExpansion: (projectKey: string, projectId: number) => void;
    onScenarioClick?: (record: import('../types').InternalScenarioRecord) => void;
    getActionMenuItems: (record: InternalTableRecord) => import('antd').MenuProps['items'];
    handleRowAction: (action: string, record: InternalTableRecord) => void;
};

export const createColumnRenderers = ({
    isProjectExpanded,
    showAnalyticsCheckbox,
    onToggleExpansion,
    onScenarioClick,
    getActionMenuItems,
    handleRowAction,
}: ColumnRenderersContext): Record<string, (record: InternalTableRecord) => React.ReactNode> => ({
    project_name: (record) => (
        <ProjectNameCell
            record={record}
            expanded={
                record.row_type === PpaTableRowType.Project ? isProjectExpanded(record.key) : false
            }
            showAnalyticsCheckbox={showAnalyticsCheckbox}
            onToggleExpansion={onToggleExpansion}
            onScenarioClick={onScenarioClick}
        />
    ),
    status: (record) => <StatusCell record={record} />,
    created_by: (record) => <CreatedByCell record={record} />,
    updated_at: (record) => getPpaColumnTextValue(record, 'last_updated'),
    simulation_period: (record) => formatPpaSimulationPeriod(record.simulation_period),
    scope: (record) => <ScopeCell record={record} />,
    action: (record) => (
        <ActionMenuCell
            record={record}
            menuItems={getActionMenuItems(record)}
            onAction={handleRowAction}
        />
    ),
});

export const renderCellValue = (
    columnId: string | undefined,
    record: InternalTableRecord | undefined,
    columnRenderers: Record<string, (record: InternalTableRecord) => React.ReactNode>,
) => {
    if (!columnId || !record) {
        return '-';
    }

    const configuredRenderer = columnRenderers[columnId];
    if (configuredRenderer) {
        return configuredRenderer(record);
    }

    const genericValue = (record as Record<string, unknown>)[columnId];
    return typeof genericValue === 'string' || typeof genericValue === 'number' ? genericValue : '-';
};
